import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { 
  FiX, 
  FiDollarSign, 
  FiCreditCard, 
  FiCalendar, 
  FiUser,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiInfo
} from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import PlantillaModal from '../util/PlantillaModal';
import SpinnerCargando from '../util/SpinnerCargando';
import { roundTo2 } from '../../miscelaneo/monetario';

// Helper para formatear moneda
function formatearMoneda(valor) {
  return roundTo2(parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// =====================================================
// COMPONENTE: Modal de Anulación
// =====================================================
function ModalAnulacion({ abierto, onCerrar, pago, onAnulado }) {
  const [razones, setRazones] = useState([]);
  const [formData, setFormData] = useState({
    id_razon_anulacion: '',
    comentario: ''
  });
  const [anulando, setAnulando] = useState(false);

  useEffect(() => {
    if (abierto) {
      // Cargar razones de anulación
      instanciaAxios.get('/pago-factura/razones-anulacion')
        .then(res => setRazones(res.data))
        .catch(() => {
          // Si no existe el endpoint, usar razones por defecto
          setRazones([
            { id_razon_anulacion: 1, descripcion: 'Error de digitación' },
            { id_razon_anulacion: 2, descripcion: 'Pago duplicado' },
            { id_razon_anulacion: 3, descripcion: 'Monto incorrecto' },
            { id_razon_anulacion: 4, descripcion: 'Método de pago incorrecto' },
            { id_razon_anulacion: 5, descripcion: 'Otro' }
          ]);
        });
    }
  }, [abierto]);

  const handleAnular = async () => {
    if (!formData.id_razon_anulacion) {
      toast.error('Seleccione una razón de anulación');
      return;
    }

    setAnulando(true);
    try {
      await instanciaAxios.put(`/pago-factura/${pago.id_pago_factura}/anular`, formData);
      toast.success('Pago anulado correctamente');
      onAnulado();
      onCerrar();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error(error.response?.data?.mensaje || 'Error al anular pago');
    } finally {
      setAnulando(false);
    }
  };

  if (!pago) return null;

  return (
    <PlantillaModal
      mostrar={abierto}
      onClose={onCerrar}
      titulo="Anular Pago"
    >
      <div className="space-y-5">
        {/* Info del pago */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="text-amber-500 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">¿Está seguro de anular este pago?</p>
              <p className="text-sm text-amber-700 mt-1">
                Monto: <span className="font-semibold">RD$ {formatearMoneda(pago.monto)}</span>
              </p>
              <p className="text-sm text-amber-700">
                Método: {pago.metodo_pago_nombre}
              </p>
              {pago.referencia && (
                <p className="text-sm text-amber-700">
                  Ref: {pago.referencia}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Razón de anulación */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Razón de Anulación *
          </label>
          <select
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            value={formData.id_razon_anulacion}
            onChange={(e) => setFormData(prev => ({ ...prev, id_razon_anulacion: e.target.value }))}
          >
            <option value="">Seleccione una razón...</option>
            {razones.map(r => (
              <option key={r.id_razon_anulacion} value={r.id_razon_anulacion}>
                {r.descripcion}
              </option>
            ))}
          </select>
        </div>

        {/* Comentario */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Comentario (opcional)
          </label>
          <textarea
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={3}
            placeholder="Detalles adicionales..."
            value={formData.comentario}
            onChange={(e) => setFormData(prev => ({ ...prev, comentario: e.target.value }))}
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onCerrar}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={anulando}
          >
            Cancelar
          </button>
          <button
            onClick={handleAnular}
            disabled={anulando}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {anulando ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Anulando...
              </>
            ) : (
              <>
                <FiXCircle size={18} />
                Confirmar Anulación
              </>
            )}
          </button>
        </div>
      </div>
    </PlantillaModal>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL: Modal Historial de Pagos
// =====================================================
export default function ModalHistorialPagos({ abierto, onCerrar, factura, onPagoAnulado }) {
  const { tienePermiso } = useContextoSesion();
  const [pagos, setPagos] = useState([]);
  const [totales, setTotales] = useState({ total_pagado: 0, total_anulado: 0, cantidad_pagos: 0 });
  const [cargando, setCargando] = useState(true);
  const [pagoAnular, setPagoAnular] = useState(null);
  const [modalAnulacionAbierto, setModalAnulacionAbierto] = useState(false);
  
  // Ref to track loaded invoice and prevent duplicate loads
  const loadedIdRef = useRef(null);

  // Cargar pagos
  const cargarPagos = useCallback(async () => {
    if (!factura?.id_factura) return;
    
    setCargando(true);
    try {
      const response = await instanciaAxios.get(`/pago-factura/factura/${factura.id_factura}`);
      setPagos(response.data.pagos || []);
      setTotales({
        total_pagado: response.data.total_pagado || 0,
        total_anulado: response.data.total_anulado || 0,
        cantidad_pagos: response.data.cantidad_pagos || 0
      });
    } catch (error) {
      console.error('Error al cargar pagos:', error);
      toast.error('Error al cargar historial de pagos');
    } finally {
      setCargando(false);
    }
  }, [factura?.id_factura]);

  useEffect(() => {
    if (abierto && factura?.id_factura && loadedIdRef.current !== factura.id_factura) {
      loadedIdRef.current = factura.id_factura;
      cargarPagos();
    }
    // Al cerrar el modal, limpiar el ref para que re-cargue datos frescos al reabrir
    if (!abierto) {
      loadedIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, factura?.id_factura]);

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handler para anulación
  const handleAnular = (pago) => {
    setPagoAnular(pago);
    setModalAnulacionAbierto(true);
  };

  const handlePagoAnulado = () => {
    cargarPagos();
    if (onPagoAnulado) {
      onPagoAnulado();
    }
  };

  // Calcular comisiones totales
  const totalComisiones = pagos
    .filter(p => !p.anulado)
    .reduce((sum, p) => sum + parseFloat(p.monto_comision || 0), 0);

  if (!factura) return null;

  return (
    <>
      <PlantillaModal
        mostrar={abierto}
        onClose={onCerrar}
        titulo="Historial de Pagos"
      >
        {/* Header con info de factura */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Factura</p>
              <p className="font-mono font-semibold text-gray-900">{factura.ncf}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Cliente</p>
              <p className="font-medium text-gray-900">{factura.cliente_nombre || 'Consumidor Final'}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Monto Total</p>
              <p className="font-semibold text-gray-900">RD$ {formatearMoneda(factura.monto_total)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Balance Pendiente</p>
              <p className={`font-bold text-lg ${parseFloat(factura.balance) > 0 ? 'text-color-primario' : 'text-green-600'}`}>
                RD$ {formatearMoneda(factura.balance)}
              </p>
            </div>
          </div>
        </div>

        {cargando ? (
          <div className="flex justify-center py-12">
            <SpinnerCargando />
          </div>
        ) : (
          <>
            {/* Resumen de pagos */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-600 mb-1">
                  <FiCheckCircle size={16} />
                  <span className="text-sm font-medium">Total Pagado</span>
                </div>
                <p className="text-xl font-bold text-emerald-700">
                  RD$ {formatearMoneda(totales.total_pagado)}
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-2 text-color-primario mb-1">
                  <FiDollarSign size={16} />
                  <span className="text-sm font-medium">Pagos Activos</span>
                </div>
                <p className="text-xl font-bold text-color-primario">
                  {totales.cantidad_pagos}
                </p>
              </div>
              
              {totalComisiones > 0 && (
                <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
                  <div className="flex items-center gap-2 text-rose-600 mb-1">
                    <FiCreditCard size={16} />
                    <span className="text-sm font-medium">Comisiones</span>
                  </div>
                  <p className="text-xl font-bold text-rose-700">
                    RD$ {formatearMoneda(totalComisiones)}
                  </p>
                </div>
              )}
              
              {totales.total_anulado > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <FiXCircle size={16} />
                    <span className="text-sm font-medium">Anulados</span>
                  </div>
                  <p className="text-xl font-bold text-gray-500">
                    RD$ {formatearMoneda(totales.total_anulado)}
                  </p>
                </div>
              )}
            </div>

            {/* Tabla de pagos */}
            {pagos.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FiDollarSign className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-500">No hay pagos registrados para esta factura</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Método
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Pasarela
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Comisión
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Referencia
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {pagos.map((pago) => (
                      <tr 
                        key={pago.id_pago_factura} 
                        className={`${pago.anulado ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <FiCalendar size={14} className="text-gray-400" />
                            {formatearFecha(pago.fecha_hora)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{pago.metodo_pago_nombre}</span>
                          <p className="text-xs text-gray-500">{pago.cuenta_tesoreria_nombre}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {pago.pasarela_nombre ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-color-primario rounded text-xs font-medium">
                              <FiCreditCard size={12} />
                              {pago.pasarela_nombre}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold ${pago.anulado ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            RD$ {formatearMoneda(pago.monto)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {parseFloat(pago.monto_comision) > 0 ? (
                            <span className="text-rose-600">
                              RD$ {formatearMoneda(pago.monto_comision)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pago.referencia || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {pago.anulado ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                <FiXCircle size={12} />
                                Anulado
                              </span>
                              {pago.razon_anulacion && (
                                <p className="text-xs text-gray-500 mt-1">{pago.razon_anulacion}</p>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                              <FiCheckCircle size={12} />
                              Activo
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!pago.anulado && tienePermiso('venta_anular_pago') && (
                            <button
                              onClick={() => handleAnular(pago)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Anular pago"
                            >
                              <FiXCircle size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Nota sobre comisiones */}
            {totalComisiones > 0 && (
              <div className="mt-4 flex items-start gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                <FiInfo className="text-gray-400 mt-0.5" size={16} />
                <p>
                  Las comisiones de pasarela (RD$ {formatearMoneda(totalComisiones)}) son asumidas por la empresa 
                  y no afectan el balance de la factura.
                </p>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 mt-4 border-t">
          <button
            onClick={onCerrar}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </PlantillaModal>

      {/* Modal de Anulación */}
      <ModalAnulacion
        abierto={modalAnulacionAbierto}
        onCerrar={() => {
          setModalAnulacionAbierto(false);
          setPagoAnular(null);
        }}
        pago={pagoAnular}
        onAnulado={handlePagoAnulado}
      />
    </>
  );
}

